import {
  forwardRef,
  type ForwardedRef,
  type ImgHTMLAttributes,
} from 'react'
import { useWechatMediaEnvironment } from '../../hooks/useWechatMediaMode'
import { responsiveImages } from '../../data/responsiveImages'
import { ProtectedImage } from '../ProtectedImage/ProtectedImage'
import styles from './ResponsiveImage.module.css'

interface ResponsiveImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string
  sizes: string
  priority?: boolean
}

export const ResponsiveImage = forwardRef<
  HTMLElement,
  ResponsiveImageProps
>(function ResponsiveImage(
  {
    src,
    sizes,
    priority = false,
    loading,
    decoding,
    fetchPriority,
    ...imageProps
  },
  ref,
) {
  const sources = responsiveImages[src]
  const { isWechat } = useWechatMediaEnvironment()

  if (isWechat) {
    return (
      <ProtectedImage
        {...imageProps}
        ref={ref}
        src={src}
        wechatSrcSet={sources?.[0]?.srcSet}
        sizes={sizes}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        decoding={decoding ?? 'async'}
        fetchPriority={priority ? 'high' : fetchPriority}
      />
    )
  }

  return (
    <picture className={styles.picture}>
      {sources?.map((source) => (
        <source
          key={source.type}
          type={source.type}
          srcSet={source.srcSet}
          sizes={sizes}
        />
      ))}
      <img
        {...imageProps}
        ref={ref as ForwardedRef<HTMLImageElement>}
        src={src}
        sizes={sizes}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        decoding={decoding ?? 'async'}
        fetchPriority={priority ? 'high' : fetchPriority}
        draggable={false}
      />
    </picture>
  )
})
